// src/events/EventOccurrenceShow.tsx
import * as React from "react";
import {
  Show,
  useRecordContext,
  useNotify,
  useDataProvider,
  useRefresh,
  usePermissions,
} from "react-admin";
import { useCreatePath } from "react-admin";
import { Link as RouterLink } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  Divider,
  TextField,
  Dialog,
  IconButton,
  Tooltip,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  useMediaQuery,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  List,
  ListItem,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import UploadIcon from "@mui/icons-material/Upload";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import LinkIcon from "@mui/icons-material/Link";
import AddIcon from "@mui/icons-material/Add";
import WorkIcon from "@mui/icons-material/Work";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlayerIdentityRow from "../profile/PlayerIdentityRow";
import { httpClient } from "../httpClient";
import { useClub } from "../club/ClubContext";
import { can } from "../auth/can";

// ✅ Backend-Origin für /static/... (wenn Frontend :3000 und Backend :8088)
// Empfehlung: in .env setzen: REACT_APP_BACKEND_ORIGIN=http://localhost:8088
const BACKEND_ORIGIN =
  (process.env.REACT_APP_BACKEND_ORIGIN as string | undefined) ||
  (process.env.REACT_APP_CDN_URL as string | undefined) ||
  "http://localhost:8088";

function resolveMediaUrl(filePath?: string) {
  if (!filePath) return "";
  if (/^https?:\/\//i.test(filePath)) return filePath;
  if (filePath.startsWith("/")) return `${BACKEND_ORIGIN}${filePath}`;
  return `${BACKEND_ORIGIN}/${filePath}`;
}

function hasTimezoneInfo(s: string) {
  // enthält Z oder +01:00 oder -0500 etc.
  return /([zZ]|[+\-]\d{2}:?\d{2})$/.test(s);
}

function parseBackendDate(s: string): Date {
  // Wenn keine TZ im String ist, behandeln wir es als UTC
  if (!hasTimezoneInfo(s)) return new Date(s + "Z");
  return new Date(s);
}

// Date (lokal) -> "YYYY-MM-DDTHH:MM" für datetime-local
function toDateTimeLocalValueFromBackend(s: string) {
  const d = parseBackendDate(s);
  const pad2 = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(
    d.getHours()
  )}:${pad2(d.getMinutes())}`;
}

// "YYYY-MM-DDTHH:MM" (lokal) -> ISO UTC fürs Backend
function dateTimeLocalToIsoUtc(v: string) {
  return new Date(v).toISOString();
}

function minutesBetweenLocalInputs(startLocal: string, endLocal: string): number | null {
  if (!startLocal || !endLocal) return null;
  const s = new Date(startLocal);
  const e = new Date(endLocal);
  const ms = e.getTime() - s.getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return Math.round(ms / 60000);
}

function sameLocalDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatDateTime(d: Date) {
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatTime(d: Date) {
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

type MediaItem = {
  id: number;
  occurrence_id?: number;
  user_id?: number;
  file_path: string;
  caption?: string | null;
  created_at?: string;
};

type JobType = {
  id: number;
  name: string;
  description?: string | null;
  active: boolean;
};

type JobAssignment = {
  id: number;
  occurrence_id: number;
  job_type_id: number | null;
  job_type_name: string;
  player_id: number;
  player_display_name: string | null;
  player_avatar_url?: string | null;
  assigned_by_display_name?: string | null;
  note?: string | null;
  created_at: string;
};

type AttendanceUser = {
  id: number;
  name?: string | null;
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  points?: number;
  avatar_url?: string | null;
};

type AttendanceRowV2 = {
  user_id: number;
  display_name: string | null;
  status: "YES" | "NO" | "MAYBE" | null;
  updated_at: string | null;
  first_name?: string | null;
  last_name?: string | null;
  points?: number;
  avatar_url?: string | null;
};

type AttendanceResponse = {
  occurrence_id?: number;

  yes?: number;
  no?: number;
  maybe?: number;
  no_response?: number;

  rows?: AttendanceRowV2[];

  items?: Array<{ user: AttendanceUser; status: string | null }>;
  yes_list?: AttendanceUser[];
  no_list?: AttendanceUser[];
  maybe_list?: AttendanceUser[];
};

function TabPanel(props: { value: number; index: number; children: React.ReactNode }) {
  const { value, index, children } = props;
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

export function EventOccurrenceShow(props: any) {
  return (
    <Show {...props}>
      <OccurrenceDetail />
    </Show>
  );
}

function OccurrenceDetail() {
  const createPath = useCreatePath();
  const r: any = useRecordContext();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const { permissions } = usePermissions();
  const { clubId } = useClub();

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [tab, setTab] = React.useState(0);

  const desc =
    String(r?.description ?? "")
      .replace(/\\n/g, "\n")
      .replace(/\r\n/g, "\n")
      .replace(/\\,/g, ",")
      .trim();

  const loc =
    String(r?.location ?? "")
      .replace(/\\n/g, "\n")
      .replace(/\r\n/g, "\n")
      .replace(/\\,/g, ",")
      .trim();

  // Upload/Gallery state (modern)
  const [caption, setCaption] = React.useState("");
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);

  const [media, setMedia] = React.useState<MediaItem[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  // Viewer / Lightbox
  const [viewerOpen, setViewerOpen] = React.useState(false);
  const [viewerIndex, setViewerIndex] = React.useState(0);
  const touchStartX = React.useRef<number | null>(null);

  // Attendance state
  const [attLoading, setAttLoading] = React.useState(false);
  const [attError, setAttError] = React.useState<string | null>(null);
  const [attData, setAttData] = React.useState<AttendanceResponse | null>(null);

  // Jobs state
  const [jobAssignments, setJobAssignments] = React.useState<JobAssignment[]>([]);
  const [jobTypes, setJobTypes] = React.useState<JobType[]>([]);
  const [jobsLoading, setJobsLoading] = React.useState(false);
  const [assignJobOpen, setAssignJobOpen] = React.useState(false);
  const [assignJobTypeId, setAssignJobTypeId] = React.useState<string>("");
  const [assignPlayerId, setAssignPlayerId] = React.useState<any>(null);
  const [assignNote, setAssignNote] = React.useState("");
  const [assignBusy, setAssignBusy] = React.useState(false);
  const [players, setPlayers] = React.useState<{ id: number; display_name: string; email: string; avatar_url?: string }[]>([]);
  const assignablePlayers = React.useMemo(() => {
    const declinedIds = new Set((attData?.rows ?? []).filter(row => row.status === "NO").map(row => row.user_id));
    return players.filter(player => !declinedIds.has(player.id));
  }, [attData?.rows, players]);

  // ✅ Edit (move/cancel) state
  const [editBusy, setEditBusy] = React.useState(false);

  // move inputs (local datetime-local)
  const [moveStartLocal, setMoveStartLocal] = React.useState<string>("");
  const [moveEndLocal, setMoveEndLocal] = React.useState<string>("");

  // bulk cancel from date
  const [cancelFromLocal, setCancelFromLocal] = React.useState<string>("");
  const [cancelBusy, setCancelBusy] = React.useState(false);

  // ✅ Ort Override
  const [overrideLocation, setOverrideLocation] = React.useState<string>("");

  // ✅ Treffpunkt-Zeit Override
  const [overrideMeetingPointLocal, setOverrideMeetingPointLocal] = React.useState<string>("");

  React.useEffect(() => {
    if (!r?.id) return;

    // defaults on record load
    if (r?.starts_at) setMoveStartLocal(toDateTimeLocalValueFromBackend(r.starts_at));
    if (r?.ends_at) setMoveEndLocal(toDateTimeLocalValueFromBackend(r.ends_at));

    setOverrideLocation(r?.location ?? "");
    setOverrideMeetingPointLocal(r?.meeting_point_at ? toDateTimeLocalValueFromBackend(r.meeting_point_at) : "");

    const anchor = r?.series_occurrence_start ?? r?.starts_at;
    if (anchor) setCancelFromLocal(toDateTimeLocalValueFromBackend(anchor));
  }, [r?.id]); // bewusst nur bei Record-Wechsel

  // ✅ anchor für Exceptions: NICHT starts_at nehmen, wenn verschoben wurde
  const getExceptionAnchorStart = React.useCallback(() => {
    return (r?.series_occurrence_start as string | undefined) ?? (r?.starts_at as string | undefined);
  }, [r]);

  const loadMedia = React.useCallback(async () => {
    if (!r?.id) return;
    const res = await dataProvider.getList(`events/occurrences/${r.id}/media`, {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "created_at", order: "DESC" },
      filter: {},
    } as any);
    setMedia((res.data ?? []) as MediaItem[]);
  }, [r?.id, dataProvider]);

  React.useEffect(() => {
    loadMedia().catch(() => {});
  }, [loadMedia]);

  const clearSelectedFile = React.useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const setFile = React.useCallback(
    (f: File | null) => {
      if (!f) {
        clearSelectedFile();
        return;
      }
      if (!f.type?.startsWith("image/")) {
        notify("Bitte eine Bilddatei auswählen.", { type: "warning" });
        return;
      }
      // optional: größen-limit (10 MB)
      const maxBytes = 10 * 1024 * 1024;
      if (f.size > maxBytes) {
        notify("Bild ist zu groß (max. 10 MB).", { type: "warning" });
        return;
      }

      setSelectedFile(f);
      const url = URL.createObjectURL(f);
      setPreviewUrl(url);
    },
    [clearSelectedFile, notify]
  );

  React.useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const f = e.dataTransfer?.files?.[0] ?? null;
    setFile(f);
  };

  const onBrowseClick = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const loadAttendance = React.useCallback(async () => {
    if (!r?.id) return;

    setAttLoading(true);
    setAttError(null);

    try {
      const res = await (dataProvider as any).custom(`events/occurrences/${r.id}/attendance`, {
        method: "GET",
      });
      setAttData((res?.data ?? null) as AttendanceResponse | null);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.warn("attendance load failed", e);
      setAttError(e?.message ?? "Konnte Zusagen nicht laden.");
      setAttData(null);
    } finally {
      setAttLoading(false);
    }
  }, [r?.id, dataProvider]);

  // ✅ Attendance nur laden, wenn Tab geöffnet wird (und noch nichts da ist)
  React.useEffect(() => {
    //if (tab !== 1) return;
    if (attData) return;
    loadAttendance().catch(() => {});
  }, [tab, attData, loadAttendance]);

  const loadJobs = React.useCallback(async () => {
    if (!r?.id) return;
    setJobsLoading(true);
    try {
      const res = await httpClient(`/api/events/occurrences/${r.id}/jobs`);
      setJobAssignments(res.json?.data ?? []);
    } catch {
      // silently ignore
    } finally {
      setJobsLoading(false);
    }
  }, [r?.id]);

  const loadJobTypes = React.useCallback(async () => {
    if (!clubId) return;
    try {
      const res = await httpClient(`/api/events/job-types?club_id=${clubId}`);
      setJobTypes((res.json?.data ?? []).filter((t: JobType) => t.active));
    } catch {
      // silently ignore
    }
  }, [clubId]);

  const loadPlayers = React.useCallback(async () => {
    if (!clubId) return;
    try {
      const res = await httpClient(`/api/users?clubId=${clubId}&perPage=500`);
      setPlayers(res.json?.data ?? []);
    } catch {
      setPlayers([]);
    }
  }, [clubId]);

  React.useEffect(() => {
    loadJobs().catch(() => {});
  }, [loadJobs]);

  const handleAssignJob = async () => {
    if (!r?.id || !assignJobTypeId || !assignPlayerId) return;
    setAssignBusy(true);
    try {
      await httpClient(`/api/events/occurrences/${r.id}/jobs`, {
        method: "POST",
        body: JSON.stringify({
          job_type_id: Number(assignJobTypeId),
          player_id: assignPlayerId.id,
          note: assignNote || undefined,
        }),
      });
      notify("Job zugewiesen");
      setAssignJobOpen(false);
      setAssignJobTypeId("");
      setAssignPlayerId(null);
      setAssignNote("");
      loadJobs();
    } catch (e: any) {
      notify(e?.body?.detail ?? "Fehler beim Zuweisen", { type: "error" });
    } finally {
      setAssignBusy(false);
    }
  };

  const handleRemoveJob = async (assignmentId: number) => {
    if (!r?.id) return;
    if (!window.confirm("Job-Einplanung wirklich entfernen? Der Spieler wird benachrichtigt.")) return;
    try {
      await httpClient(`/api/events/occurrences/${r.id}/jobs/${assignmentId}`, { method: "DELETE" });
      notify("Job-Einplanung entfernt");
      loadJobs();
    } catch {
      notify("Fehler beim Entfernen", { type: "error" });
    }
  };

  const openAssignDialog = () => {
    loadJobTypes();
    loadPlayers();
    setAssignJobOpen(true);
  };

  const upload = async () => {
    if (!r?.id || !selectedFile) return;

    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", selectedFile);
      if (caption) form.append("caption", caption);

      await (dataProvider as any).create(`events/occurrences/${r.id}/media`, {
        data: form,
      });

      setCaption("");
      clearSelectedFile();

      await loadMedia();
      refresh();
      notify("Bild hochgeladen", { type: "info" });
    } catch (e: any) {
      notify(e?.message ?? "Upload fehlgeschlagen", { type: "warning" });
    } finally {
      setUploading(false);
    }
  };

  const openViewer = (idx: number) => {
    setViewerIndex(idx);
    setViewerOpen(true);
  };
  const closeViewer = () => setViewerOpen(false);

  const prev = React.useCallback(() => {
    setViewerIndex((i) => (i <= 0 ? media.length - 1 : i - 1));
  }, [media.length]);

  const next = React.useCallback(() => {
    setViewerIndex((i) => (i >= media.length - 1 ? 0 : i + 1));
  }, [media.length]);

  React.useEffect(() => {
    if (!viewerOpen) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") closeViewer();
      if (ev.key === "ArrowLeft") prev();
      if (ev.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewerOpen, prev, next]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartX.current;
    if (start == null) return;
    const end = e.changedTouches?.[0]?.clientX ?? start;
    const dx = end - start;
    touchStartX.current = null;

    if (Math.abs(dx) < 60) return;
    if (dx > 0) prev();
    else next();
  };

  const deleteMedia = async (item: MediaItem) => {
    if (!item?.id) return;

    const ok = window.confirm("Bild wirklich löschen?");
    if (!ok) return;

    try {
      await (dataProvider as any).custom(`events/media/${item.id}`, {
        method: "DELETE",
      });

      setMedia((cur) => cur.filter((x) => x.id !== item.id));
      notify("Bild gelöscht", { type: "info" });

      if (viewerOpen) {
        const newLen = media.length - 1;
        if (newLen <= 0) setViewerOpen(false);
        else if (viewerIndex >= newLen) setViewerIndex(newLen - 1);
      }
    } catch (e: any) {
      notify(e?.message ?? "Löschen fehlgeschlagen", { type: "warning" });
    }
  };

  const attBuckets = React.useMemo<{
    yes: AttendanceUser[];
    no: AttendanceUser[];
    maybe: AttendanceUser[];
    no_response: AttendanceUser[];
  }>(() => {
    const empty = {
      yes: [] as AttendanceUser[],
      no: [] as AttendanceUser[],
      maybe: [] as AttendanceUser[],
      no_response: [] as AttendanceUser[],
    };

    if (!attData) return empty;

    if (Array.isArray(attData.rows)) {
      const b = { ...empty };
      for (const row of attData.rows) {
        const u: AttendanceUser = {
          id: row.user_id,
          display_name: row.display_name,
          first_name: row.first_name,
          last_name: row.last_name,
          points: row.points,
          avatar_url: row.avatar_url,
        };

        if (row.status === "YES") b.yes.push(u);
        else if (row.status === "NO") b.no.push(u);
        else if (row.status === "MAYBE") b.maybe.push(u);
        else b.no_response.push(u);
      }
      return b;
    }

    const items = Array.isArray(attData.items) ? attData.items : null;
    if (items) {
      const b = { ...empty };
      for (const it of items) {
        const s = String(it.status ?? "").toUpperCase();
        const u = it.user as AttendanceUser;
        if (!u) continue;

        if (s === "YES") b.yes.push(u);
        else if (s === "NO") b.no.push(u);
        else if (s === "MAYBE") b.maybe.push(u);
        else b.no_response.push(u);
      }
      return b;
    }

    return empty;
  }, [attData]);

  const attCounts = React.useMemo(() => {
    const yes = typeof attData?.yes === "number" ? attData!.yes : attBuckets.yes.length;
    const no = typeof attData?.no === "number" ? attData!.no : attBuckets.no.length;
    const maybe = typeof attData?.maybe === "number" ? attData!.maybe : attBuckets.maybe.length;
    const no_response =
      typeof attData?.no_response === "number"
        ? attData!.no_response
        : attBuckets.no_response.length;

    return { yes, no, maybe, no_response };
  }, [attData, attBuckets]);

  if (!r) return null;

  const dt = r.starts_at ? parseBackendDate(r.starts_at) : null;
  const dtEnd = r.ends_at ? parseBackendDate(r.ends_at) : null;
  const isAllClub = !r.team_ids?.length;

  const cols = isXs ? 2 : 4;
  const active = media[viewerIndex];

  const showAttendanceTab = true;
  const showJobsTab = true;
  const showEditTab = can(permissions, "events", "edit");
  const canManageJobs = can(permissions, "events", "edit");

  const idxAttendance = showAttendanceTab ? 1 : -1;
  const idxJobs = (() => {
    let idx = 1;
    if (showAttendanceTab) idx++;
    return idx;
  })();
  const idxEdit = showEditTab ? idxJobs + 1 : -1;

  const isMultiDay = dt && dtEnd ? !sameLocalDay(dt, dtEnd) : false;

  return (
    <Card sx={{ overflow: "hidden" }}>
      <CardContent sx={{ overflowX: "hidden" }}>
        <Stack spacing={2}>
          {/* Header */}
          <Stack spacing={0.5}>
            <Button
              component={RouterLink}
              to={createPath({ resource: "events/occurrences", type: "list" })}
              startIcon={<ArrowBackIcon />}
              size="small"
              sx={{ alignSelf: "flex-start", mb: 0.5 }}
            >
              Zurück zur Liste
            </Button>

            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              {r.title}
            </Typography>

            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {dt ? (
                dtEnd ? (
                  isMultiDay ? (
                    <>
                      {formatDateTime(dt)} → {formatDateTime(dtEnd)}{" "}
                      {r.timezone ? `(${r.timezone})` : ""}
                    </>
                  ) : (
                    <>
                      {formatDateTime(dt)} – {formatTime(dtEnd)}{" "}
                      {r.timezone ? `(${r.timezone})` : ""}
                    </>
                  )
                ) : (
                  <>
                    {formatDateTime(dt)} {r.timezone ? `(${r.timezone})` : ""}
                  </>
                )
              ) : (
                "—"
              )}
              {isMultiDay && <Chip size="small" variant="outlined" label="Mehrtag" sx={{ ml: 1 }} />}
            </Typography>

            {loc && (
              <Typography variant="body2" sx={{ opacity: 0.85, whiteSpace: "pre-line" }}>
                📍 {loc}
              </Typography>
            )}

            {r.meeting_point_at && (
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                🕒 Treffpunkt: {formatTime(parseBackendDate(r.meeting_point_at))} Uhr
              </Typography>
            )}

            {isAllClub ? (
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", mt: 0.5 }}>
                <Chip size="small" label="Alle Teams" sx={{ mt: 0.5 }} />
              </Stack>
            ) : (r.team_names?.length ?? 0) > 0 ? (
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", mt: 0.5 }}>
                {(r.team_names ?? []).map((t: string) => (
                  <Chip key={t} size="small" label={t} />
                ))}
              </Stack>
            ) : (
              <Chip
                size="small"
                label="(keine Teams zugeordnet)"
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            )}

            {desc && (
              <Typography variant="body2" sx={{ whiteSpace: "pre-line", mt: 1 }}>
                {desc}
              </Typography>
            )}

            {r.link_url && (
              <Box sx={{ mt: 1 }}>
                <Button size="small" startIcon={<LinkIcon />} href={r.link_url} target="_blank" rel="noreferrer">
                  Link öffnen
                </Button>
              </Box>
            )}
          </Stack>

          <Divider />

          {/* Tabs */}
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
            <Tab label={`Bilder (${media.length})`} />
            {showAttendanceTab && (
              <Tab
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>Zusagen</span>
                    <Chip size="small" label={attCounts.yes} />
                  </Stack>
                }
              />
            )}
            {showJobsTab && (
              <Tab
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>Jobs</span>
                    {jobAssignments.length > 0 && <Chip size="small" label={jobAssignments.length} />}
                  </Stack>
                }
              />
            )}
            {showEditTab && <Tab label="Bearbeiten" />}
          </Tabs>

          {/* TAB: Bilder */}
          <TabPanel value={tab} index={0}>
            <Stack spacing={2}>
              {/* Modern Upload */}
              <Stack spacing={1.5} sx={{ maxWidth: 1100 }}>
                <Box
                  onClick={onBrowseClick}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragOver(true);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragOver(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragOver(false);
                  }}
                  onDrop={onDrop}
                  sx={{
                    position: "relative",
                    border: "2px dashed",
                    borderColor: isDragOver ? "primary.main" : "divider",
                    borderRadius: 3,
                    p: 2,
                    cursor: uploading ? "not-allowed" : "pointer",
                    userSelect: "none",
                    bgcolor: isDragOver ? "action.hover" : "background.default",
                    transition: "all .15s",
                    "&:hover": uploading
                      ? undefined
                      : {
                          bgcolor: "action.hover",
                          borderColor: "primary.main",
                        },
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    disabled={uploading}
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />

                  {!previewUrl ? (
                    <Stack spacing={0.75} alignItems="center" textAlign="center">
                      <UploadIcon fontSize="large" color="action" />
                      <Typography fontWeight={800}>
                        Bild auswählen oder hierher ziehen
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        JPG/PNG · max. 10 MB
                      </Typography>

                      {isDragOver && (
                        <Chip size="small" color="primary" label="Loslassen zum Auswählen" />
                      )}
                    </Stack>
                  ) : (
                    <Stack spacing={1} alignItems="center">
                      <Box
                        component="img"
                        src={previewUrl}
                        alt="Preview"
                        sx={{
                          maxHeight: 220,
                          width: "100%",
                          maxWidth: 520,
                          borderRadius: 2,
                          boxShadow: 3,
                          objectFit: "cover",
                        }}
                      />
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          variant="outlined"
                          label={selectedFile?.name ?? "Bild ausgewählt"}
                        />
                        <Button
                          size="small"
                          disabled={uploading}
                          onClick={(e) => {
                            e.stopPropagation();
                            clearSelectedFile();
                          }}
                        >
                          Entfernen
                        </Button>
                      </Stack>
                    </Stack>
                  )}

                  {uploading && (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 3,
                        bgcolor: "rgba(0,0,0,0.18)",
                        backdropFilter: "blur(2px)",
                      }}
                    >
                      <Stack spacing={1} alignItems="center">
                        <CircularProgress size={26} />
                        <Typography variant="body2" sx={{ opacity: 0.85 }}>
                          Upload läuft…
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </Box>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "stretch", sm: "center" }}
                >
                  <TextField
                    size="small"
                    label="Bildbeschreibung (optional)"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    fullWidth
                    disabled={uploading}
                  />

                  <Button
                    variant="contained"
                    startIcon={<UploadIcon />}
                    disabled={uploading || !selectedFile}
                    onClick={upload}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Hochladen
                  </Button>
                </Stack>
              </Stack>

              {/* Gallery */}
              {media.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  Noch keine Bilder vorhanden.
                </Typography>
              ) : (
                <ImageList variant="masonry" cols={cols} gap={10} sx={{ mt: 1, maxWidth: 1100 }}>
                  {media.map((m, idx) => (
                    <ImageListItem key={m.id} sx={{ cursor: "pointer" }}>
                      <Box
                        sx={{
                          height: isXs ? 110 : 140,
                          overflow: "hidden",
                          borderRadius: 2,
                          bgcolor: "action.hover",
                        }}
                        onClick={() => openViewer(idx)}
                      >
                        <img
                          src={resolveMediaUrl(m.file_path)}
                          alt={m.caption ?? ""}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      </Box>

                      <ImageListItemBar
                        title={m.caption ?? "—"}
                        actionIcon={
                          <Stack direction="row" spacing={0.5} sx={{ pr: 0.5 }}>
                            <Tooltip title="Ansehen">
                              <IconButton
                                sx={{ color: "white" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openViewer(idx);
                                }}
                              >
                                <ZoomInIcon />
                              </IconButton>
                            </Tooltip>

                            {can(permissions, "events", "imagedelete") && (
                              <Tooltip title="Löschen">
                                <IconButton
                                  sx={{ color: "white" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteMedia(m);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        }
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              )}

              {/* Lightbox / Viewer – fullscreen ohne Scrollbars */}
              <Dialog
                open={viewerOpen}
                onClose={closeViewer}
                fullScreen
                PaperProps={{
                  sx: {
                    bgcolor: "#111",
                    m: 0,
                    width: "100%",
                    height: "100%",
                    maxWidth: "none",
                    maxHeight: "none",
                    overflow: "hidden",
                  },
                }}
              >
                <Box
                  sx={{
                    position: "fixed",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "#111",
                    overflow: "hidden",
                  }}
                  onTouchStart={onTouchStart}
                  onTouchEnd={onTouchEnd}
                >
                  {/* Top Bar */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 1,
                      py: 0.5,
                      color: "white",
                    }}
                  >
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {active ? `${viewerIndex + 1} / ${media.length}` : ""}
                    </Typography>

                    <Stack direction="row" spacing={0.5}>
                      {active && can(permissions, "events", "imagedelete") && (
                        <Tooltip title="Löschen">
                          <IconButton sx={{ color: "white" }} onClick={() => active && deleteMedia(active)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Schließen">
                        <IconButton sx={{ color: "white" }} onClick={closeViewer}>
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>

                  {/* Image Area */}
                  <Box sx={{ flex: 1, position: "relative", overflow: "hidden" }}>
                    {!isXs && media.length > 1 && (
                      <IconButton
                        sx={{
                          position: "absolute",
                          left: 16,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "white",
                          bgcolor: "rgba(255,255,255,0.12)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                          zIndex: 2,
                        }}
                        onClick={prev}
                      >
                        <NavigateBeforeIcon />
                      </IconButton>
                    )}

                    {active && (
                      <img
                        src={resolveMediaUrl(active.file_path)}
                        alt={active.caption ?? ""}
                        draggable={false}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          userSelect: "none",
                          display: "block",
                        }}
                      />
                    )}

                    {!isXs && media.length > 1 && (
                      <IconButton
                        sx={{
                          position: "absolute",
                          right: 16,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "white",
                          bgcolor: "rgba(255,255,255,0.12)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                          zIndex: 2,
                        }}
                        onClick={next}
                      >
                        <NavigateNextIcon />
                      </IconButton>
                    )}
                  </Box>

                  {/* Caption */}
                  {active && (
                    <Box sx={{ px: 2, pb: 1, color: "white" }}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {active.caption ?? "—"}
                      </Typography>
                      {isXs && media.length > 1 && (
                        <Typography variant="caption" sx={{ opacity: 0.65 }}>
                          Swipe links/rechts zum Wechseln
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Dialog>
            </Stack>
          </TabPanel>

          {/* TAB: Zusagen */}
          {showAttendanceTab && (
            <TabPanel value={tab} index={idxAttendance}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button size="small" variant="outlined" onClick={() => loadAttendance()} disabled={attLoading}>
                    Aktualisieren
                  </Button>
                  {attLoading && <CircularProgress size={18} />}
                  {attError && (
                    <Typography variant="body2" sx={{ color: "warning.main" }}>
                      {attError}
                    </Typography>
                  )}
                </Stack>

                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                  <Chip label={`✅ Zusagen: ${attCounts.yes}`} />
                  <Chip label={`❓ Vielleicht: ${attCounts.maybe}`} />
                  <Chip label={`❌ Absagen: ${attCounts.no}`} />
                  <Chip label={`⏳ Ohne Antwort: ${attCounts.no_response}`} />
                </Stack>

                <Divider />

                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <Card sx={{ flex: 1 }}>
                    <CardContent>
                      <Typography sx={{ fontWeight: 800, mb: 1 }}>✅ Zusagen</Typography>
                      {attBuckets.yes.length === 0 ? (
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>—</Typography>
                      ) : (
                        <List dense>
                          {attBuckets.yes.map((u: AttendanceUser) => (
                            <ListItem key={`yes-${u.id}`} disableGutters>
                              <PlayerIdentityRow
                                firstName={u.first_name}
                                lastName={u.last_name}
                                email={undefined}
                                points={u.points}
                                avatarUrl={u.avatar_url}
                                showPoints={false}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </CardContent>
                  </Card>

                  <Card sx={{ flex: 1 }}>
                    <CardContent>
                      <Typography sx={{ fontWeight: 800, mb: 1 }}>❓ Vielleicht</Typography>
                      {attBuckets.maybe.length === 0 ? (
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>—</Typography>
                      ) : (
                        <List dense>
                          {attBuckets.maybe.map((u: AttendanceUser) => (
                            <ListItem key={`maybe-${u.id}`} disableGutters>
                              <PlayerIdentityRow
                                firstName={u.first_name}
                                lastName={u.last_name}
                                email={undefined}
                                points={u.points}
                                avatarUrl={u.avatar_url}
                                showPoints={false}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </CardContent>
                  </Card>

                  <Card sx={{ flex: 1 }}>
                    <CardContent>
                      <Typography sx={{ fontWeight: 800, mb: 1 }}>❌ Absagen</Typography>
                      {attBuckets.no.length === 0 ? (
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>—</Typography>
                      ) : (
                        <List dense>
                          {attBuckets.no.map((u: AttendanceUser) => (
                            <ListItem key={`no-${u.id}`} disableGutters>
                              <PlayerIdentityRow
                                firstName={u.first_name}
                                lastName={u.last_name}
                                email={undefined}
                                points={u.points}
                                avatarUrl={u.avatar_url}
                                showPoints={false}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </CardContent>
                  </Card>

                  <Card sx={{ flex: 1 }}>
                    <CardContent>
                      <Typography sx={{ fontWeight: 800, mb: 1 }}>⏳ Ohne Antwort</Typography>
                      {attBuckets.no_response.length === 0 ? (
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>—</Typography>
                      ) : (
                        <List dense>
                          {attBuckets.no_response.map((u: AttendanceUser) => (
                            <ListItem key={`nr-${u.id}`} disableGutters>
                              <PlayerIdentityRow
                                firstName={u.first_name}
                                lastName={u.last_name}
                                email={undefined}
                                points={u.points}
                                avatarUrl={u.avatar_url}
                                showPoints={false}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </CardContent>
                  </Card>
                </Stack>
              </Stack>
            </TabPanel>
          )}

          {/* TAB: Jobs */}
          {showJobsTab && (
            <TabPanel value={tab} index={idxJobs}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" fontWeight={700} display="flex" alignItems="center" gap={1}>
                    <WorkIcon fontSize="small" /> Jobliste
                  </Typography>
                  {canManageJobs && (
                    <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAssignDialog}>
                      Job zuweisen
                    </Button>
                  )}
                </Stack>

                {jobsLoading ? (
                  <Box display="flex" justifyContent="center"><CircularProgress size={24} /></Box>
                ) : jobAssignments.length === 0 ? (
                  <Box textAlign="center" py={4}>
                    <WorkIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                    <Typography color="text.secondary">Noch keine Jobs zugewiesen.</Typography>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {jobAssignments.map(a => (
                      <Card key={a.id} sx={{ borderLeft: "4px solid", borderLeftColor: "primary.main" }}>
                        <CardContent sx={{ pb: "12px !important" }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Avatar src={a.player_avatar_url ?? undefined} sx={{ width: 32, height: 32, fontSize: 13 }}>
                                {a.player_display_name?.[0]}
                              </Avatar>
                              <Box>
                                <Typography fontWeight={600} variant="body2">{a.player_display_name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {a.job_type_name}
                                  {a.assigned_by_display_name && ` · von ${a.assigned_by_display_name}`}
                                </Typography>
                              </Box>
                            </Box>
                            {canManageJobs && (
                              <Tooltip title="Einplanung entfernen">
                                <IconButton size="small" color="error" onClick={() => handleRemoveJob(a.id)}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}

                {/* Assign Job Dialog */}
                <Dialog open={assignJobOpen} onClose={() => setAssignJobOpen(false)} maxWidth="sm" fullWidth>
                  <DialogTitle>Job zuweisen</DialogTitle>
                  <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
                    <FormControl fullWidth>
                      <InputLabel>Job-Typ</InputLabel>
                      <Select
                        value={assignJobTypeId}
                        onChange={e => setAssignJobTypeId(e.target.value)}
                        label="Job-Typ"
                      >
                        {jobTypes.map(jt => (
                          <MenuItem key={jt.id} value={String(jt.id)}>{jt.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Autocomplete
                      options={assignablePlayers}
                      getOptionLabel={o => `${o.display_name} (${o.email})`}
                      value={assignPlayerId}
                      onChange={(_, v) => setAssignPlayerId(v)}
                      renderOption={(props, o) => (
                        <Box component="li" {...props} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                          <Avatar src={o.avatar_url} sx={{ width: 26, height: 26, fontSize: 11 }}>{o.display_name?.[0]}</Avatar>
                          <Box>
                            <Typography variant="body2">{o.display_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{o.email}</Typography>
                          </Box>
                        </Box>
                      )}
                      renderInput={params => <TextField {...params} label="Spieler" />}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setAssignJobOpen(false)}>Abbrechen</Button>
                    <Button
                      onClick={handleAssignJob}
                      variant="contained"
                      disabled={!assignJobTypeId || !assignPlayerId || assignBusy}
                    >
                      {assignBusy ? <CircularProgress size={18} /> : "Zuweisen"}
                    </Button>
                  </DialogActions>
                </Dialog>
              </Stack>
            </TabPanel>
          )}

          {/* TAB: Bearbeiten */}
          {showEditTab && (
            <TabPanel value={tab} index={idxEdit}>
              <Stack spacing={2} sx={{ maxWidth: 720 }}>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  Termin bearbeiten
                </Typography>

                {!r?.series_id && (
                  <Typography variant="body2" sx={{ opacity: 0.75 }}>
                    Hinweis: Dieser Termin hat keine Serie (series_id fehlt). Verschieben/Absagen ist über Serien-Exceptions gedacht.
                  </Typography>
                )}

                <Divider />

                <Typography sx={{ fontWeight: 800 }}>Verschieben</Typography>

                <TextField
                  label="Neuer Start (Datum/Uhrzeit)"
                  type="datetime-local"
                  value={moveStartLocal}
                  onChange={(e) => setMoveStartLocal(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={editBusy}
                  fullWidth
                />

                <TextField
                  label="Neues Ende (Datum/Uhrzeit)"
                  type="datetime-local"
                  value={moveEndLocal}
                  onChange={(e) => setMoveEndLocal(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={editBusy}
                  fullWidth
                  helperText="Optional. Für Mehrtag: Ende setzen. Für normalen Termin: Ende bleibt Start + Dauer."
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
                  <Button
                    variant="contained"
                    disabled={editBusy || !r?.series_id || !moveStartLocal}
                    onClick={async () => {
                      if (!r?.series_id) return;
                      if (!moveStartLocal) return;

                      const ok = window.confirm("Termin wirklich verschieben?");
                      if (!ok) return;

                      setEditBusy(true);
                      try {
                        const anchor = getExceptionAnchorStart();
                        if (!anchor) throw new Error("Kein occurrence_start (Anchor) gefunden.");

                        const payload: any = {
                          occurrence_start: anchor,
                          kind: "OVERRIDDEN",
                          override_starts_at: dateTimeLocalToIsoUtc(moveStartLocal),
                        };

                        // Wenn Ende gesetzt: duration berechnen (kann > 24h bei Einzelterminen)
                        if (moveEndLocal) {
                          const mins = minutesBetweenLocalInputs(moveStartLocal, moveEndLocal);
                          if (mins == null) throw new Error("Ende muss nach Start liegen.");
                          payload.override_duration_minutes = mins;
                        }

                        await (dataProvider as any).custom(`events/series/${r.series_id}/exceptions`, {
                          method: "POST",
                          body: JSON.stringify(payload),
                          headers: new Headers({ "Content-Type": "application/json" }),
                        });

                        notify("Termin verschoben.", { type: "info" });
                        refresh();
                      } catch (e: any) {
                        notify(e?.message ?? "Verschieben fehlgeschlagen", { type: "warning" });
                      } finally {
                        setEditBusy(false);
                      }
                    }}
                  >
                    Verschieben
                  </Button>

                  <Button
                    variant="outlined"
                    color="error"
                    disabled={editBusy || !r?.series_id}
                    onClick={async () => {
                      if (!r?.series_id) return;

                      const ok = window.confirm("Termin wirklich absagen?");
                      if (!ok) return;

                      setEditBusy(true);
                      try {
                        const anchor = getExceptionAnchorStart();
                        if (!anchor) throw new Error("Kein occurrence_start (Anchor) gefunden.");

                        const payload = {
                          occurrence_start: anchor,
                          kind: "CANCELLED",
                        };

                        await (dataProvider as any).custom(`events/series/${r.series_id}/exceptions`, {
                          method: "POST",
                          body: JSON.stringify(payload),
                          headers: new Headers({ "Content-Type": "application/json" }),
                        });

                        notify("Termin abgesagt.", { type: "info" });
                        refresh();
                      } catch (e: any) {
                        notify(e?.message ?? "Absagen fehlgeschlagen", { type: "warning" });
                      } finally {
                        setEditBusy(false);
                      }
                    }}
                  >
                    Absagen
                  </Button>

                  {editBusy && <CircularProgress size={18} />}
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Typography sx={{ fontWeight: 800 }}>Spielort überschreiben</Typography>

                <TextField
                  label="Spielort (Override)"
                  value={overrideLocation}
                  onChange={(e) => setOverrideLocation(e.target.value)}
                  disabled={editBusy}
                  fullWidth
                  placeholder="z. B. Vereinsheim, Musterstraße 1, 12345 Ort"
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
                  <Button
                    variant="contained"
                    disabled={editBusy || !r?.series_id}
                    onClick={async () => {
                      if (!r?.series_id) return;

                      const anchor = getExceptionAnchorStart();
                      if (!anchor) {
                        notify("Kein occurrence_start (Anchor) gefunden.", { type: "warning" });
                        return;
                      }

                      const ok = window.confirm("Spielort für diesen Termin wirklich ändern?");
                      if (!ok) return;

                      setEditBusy(true);
                      try {
                        const payload: any = {
                          occurrence_start: anchor,
                          kind: "OVERRIDDEN",
                          override_location: overrideLocation?.trim() || null,
                        };

                        await (dataProvider as any).custom(`events/series/${r.series_id}/exceptions`, {
                          method: "POST",
                          body: JSON.stringify(payload),
                          headers: new Headers({ "Content-Type": "application/json" }),
                        });

                        notify("Spielort gespeichert.", { type: "info" });
                        refresh();
                      } catch (e: any) {
                        notify(e?.message ?? "Spielort speichern fehlgeschlagen", { type: "warning" });
                      } finally {
                        setEditBusy(false);
                      }
                    }}
                  >
                    Ort setzen
                  </Button>

                  {editBusy && <CircularProgress size={18} />}
                </Stack>

                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                  Tipp: Der Ort erscheint als „Ort" im Kalender (iCal LOCATION).
                </Typography>

                <Divider sx={{ my: 1 }} />

                <Typography sx={{ fontWeight: 800 }}>Treffpunkt-Zeit überschreiben</Typography>

                <TextField
                  label="Treffpunkt-Zeit (Override)"
                  type="datetime-local"
                  value={overrideMeetingPointLocal}
                  onChange={(e) => setOverrideMeetingPointLocal(e.target.value)}
                  disabled={editBusy}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
                  <Button
                    variant="contained"
                    disabled={editBusy || !r?.series_id}
                    onClick={async () => {
                      if (!r?.series_id) return;

                      const anchor = getExceptionAnchorStart();
                      if (!anchor) {
                        notify("Kein occurrence_start (Anchor) gefunden.", { type: "warning" });
                        return;
                      }

                      const ok = window.confirm("Treffpunkt-Zeit für diesen Termin wirklich ändern?");
                      if (!ok) return;

                      setEditBusy(true);
                      try {
                        const payload: any = {
                          occurrence_start: anchor,
                          kind: "OVERRIDDEN",
                          override_meeting_point_at: overrideMeetingPointLocal
                            ? dateTimeLocalToIsoUtc(overrideMeetingPointLocal)
                            : null,
                        };

                        await (dataProvider as any).custom(`events/series/${r.series_id}/exceptions`, {
                          method: "POST",
                          body: JSON.stringify(payload),
                          headers: new Headers({ "Content-Type": "application/json" }),
                        });

                        notify("Treffpunkt-Zeit gespeichert.", { type: "info" });
                        refresh();
                      } catch (e: any) {
                        notify(e?.message ?? "Treffpunkt-Zeit speichern fehlgeschlagen", { type: "warning" });
                      } finally {
                        setEditBusy(false);
                      }
                    }}
                  >
                    Treffpunkt-Zeit setzen
                  </Button>

                  {editBusy && <CircularProgress size={18} />}
                </Stack>

                {r?.series_id && (
                  <>
                    <Divider sx={{ my: 1 }} />

                    <Typography sx={{ fontWeight: 800, color: "error.main" }}>
                      Alle Folgetermine absagen
                    </Typography>

                    <TextField
                      label="Absagen ab (Datum/Uhrzeit)"
                      type="datetime-local"
                      value={cancelFromLocal}
                      onChange={(e) => setCancelFromLocal(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={cancelBusy}
                      fullWidth
                      helperText="Alle Serientermine ab diesem Zeitpunkt (inkl.) werden abgesagt."
                    />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={cancelBusy || !cancelFromLocal}
                        onClick={async () => {
                          if (!r?.series_id || !cancelFromLocal) return;
                          const ok = window.confirm(
                            "Alle Serientermine ab dem gewählten Datum absagen? Das Serien-Ende wird neu gesetzt und lässt sich nur manuell rückgängig machen."
                          );
                          if (!ok) return;
                          setCancelBusy(true);
                          try {
                            await (dataProvider as any).custom(
                              `events/series/${r.series_id}/cancel-from`,
                              {
                                method: "POST",
                                body: JSON.stringify({ from_date: dateTimeLocalToIsoUtc(cancelFromLocal) }),
                                headers: new Headers({ "Content-Type": "application/json" }),
                              }
                            );
                            notify("Serientermine abgesagt.", { type: "info" });
                            refresh();
                          } catch (e: any) {
                            notify(e?.message ?? "Fehler beim Absagen", { type: "warning" });
                          } finally {
                            setCancelBusy(false);
                          }
                        }}
                      >
                        Alle ab Datum absagen
                      </Button>
                      {cancelBusy && <CircularProgress size={18} />}
                    </Stack>
                  </>
                )}
              </Stack>
            </TabPanel>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
